import React from 'react';
import { render } from 'react-dom';
import { AppContainer } from 'react-hot-loader';
import { BrowserRouter as Router } from 'react-router-dom';
import IndexedDB from './services/IndexedDB';
import rootReducer from './store/Reducers';
import store from './store/Store';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'react-redux';
import App from './components/Application';
import i18n from './i18n';
import './index.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { AuthContext } from './services/config/AdalConfig';
import Api from './services/Api';
import AdalConfig from './services/config/AdalEnvConfig';
import SessionHelper from './services/Session';
import LoginLight from './components/LoginLight/LoginComponent';
import LoginUserInactive from './components/LoginLight/LoginUserInactive';
import CodeValuesDB from './services/IndexedDB/CodeValuesDB';
import * as urls from './components/shared/urls';
import { getActiveTypes } from './components/LookupValues/redux-objects/actions';
import { createLocationAuditLog } from './components/Application/redux-objects/actions';
import { ILocationLog } from './common/logInterfaces';
import { ElasticIndices, LogCategoryEnum, LoggingLevel, } from './services/Enums/shared';

/** Easy Query CSS Style Imports */
import '@easyquery/ui/dist/assets/css/easyquery.css';
import '@easyquery/ui/dist/assets/css/eq-icons-default.css';
import '@easyquery/ui/dist/assets/css/easyquery-mobile.css';
import '@easyquery/ui/dist/assets/css/eqview.css';
import '@easyquery/ui/dist/assets/css/easychart.css';
import '@easyquery/ui/dist/assets/css/easyfacets.css';
import '@easydata/ui/dist/assets/css/easy-grid.css';
import '@easydata/ui/dist/assets/css/easy-dialog.css';
import '@easydata/ui/dist/assets/css/easy-forms.css';
import StandardSummaryViewer from './components/StandardSummaryViewer/StandardSummaryViewer';
import StandardSummaryFatalityReportViewer from './components/StandardSummaryViewer/StandardSummaryFatalityReportViewer';
import StandardSummaryTroopAreaReportViewer from './components/StandardSummaryViewer/StandardSummaryTroopAreaReportViewer';

const rootEl = document.getElementById('root');

const reduxStore = store({}, rootReducer);

declare global {
  interface Window {
    EQ: any;
  }
}

function sortByKey(array: any, key: string) {
  return array.sort(function (a, b) {
    let x = a[key],
      y = b[key];
    if (typeof x === 'string') {
      x = x.toLowerCase();
      y = y.toLowerCase();
      if (!isNaN(x) && !isNaN(y)) {
        x = parseInt(x, 10);
        y = parseInt(y, 10);
      }
    }
    return (x < y ? -1 : (x > y ? 1 : 0));
  });
}

class DBWrapper extends React.Component<any, any> {
  constructor(props) {
    super(props);
    this.state = {
      dbInitComplete: false,
      loadingCodeValues: false,
      codeValuesUpdating: false,
    };
  }

  componentDidMount() {
    let types;
    let reduxStore = this.props.reduxStore;
    const backendUrl = reduxStore.getState().Application.backendURL;
    const typeUrl = backendUrl + urls.GET_ACTVIE_TYPES_URL;
    this.loadGoogleMapsApiKey();
    IndexedDB.initDb().then(async () => {
      this.setState({ dbInitComplete: true });
      this.setState({ loadingCodeValues: true });
      reduxStore.dispatch(getActiveTypes(typeUrl)).then(async () => {
        this.setState({ loadingCodeValues: false });
        this.setState({ codeValuesUpdating: true });
        types = reduxStore.getState().LookupTypes.activeTypes;
        Object.keys(types).map(item => {
          let unsorted = types[item];
          return sortByKey(unsorted, 'name');
        });      
        await CodeValuesDB.AddCodeValuesEntriesToDB(types);
        this.setState({ codeValuesUpdating: false });
      });
    });

    document.addEventListener('location-updated', this.createLocationLog);
  }

  loadGoogleMapsApiKey = async () => {
    try {
      const response = await Api.get(`${urls.GET_SETTING_URL}?name=GoogleMapsApiKey`);
      if (!response.ok || !response.data) {
        return;
      }
      const setting: any = response.data;
      const apiKey = setting.Value ?? setting.value;
      if (apiKey) {
        (window as any).googleMapsApiKey = apiKey;
      }
    } catch {
      // Non-fatal: Google Maps features will fall back to no key if this fails.
    }
  };

  sorter = () => {

  }

  createLocationLog = (event) => {
    const newLocationLog: ILocationLog = {
      elasticIndex: ElasticIndices.Audit,
      logCategory: LogCategoryEnum.LocationUpdated,
      logLevel: LoggingLevel.Audit,
      location: (event as CustomEvent).detail.location,
      reportId: (event as CustomEvent).detail.reportId,
    };
    reduxStore.dispatch(createLocationAuditLog(newLocationLog));
  };

  handleSignOut = () => { };

  componentWillUnmount() {
    document.removeEventListener('location-updated', this.createLocationLog);
  }

  render() {
    const {
      dbInitComplete,
      loadingCodeValues,
      codeValuesUpdating,
      allTypes,
    } = this.state;
    return (
      <React.Fragment>
        {dbInitComplete ? (
          this.props.children
        ) : (
          <div>Initializing Db...Please wait</div>
        )}
      </React.Fragment>
    );
  }
}

// Handle possible callbacks on id_token or access_token
AuthContext.handleWindowCallback();

if (window.location.pathname == '/signedout') {
  render(<LoginLight />, rootEl);
}if (window.location.pathname == '/deactivated') {
  render(<LoginUserInactive />, rootEl);
} else if (window.location.pathname == '/StandardSummaryViewer') {
  render(
    <DBWrapper reduxStore={reduxStore}>
      <StandardSummaryViewer />
    </DBWrapper>
    , rootEl);
} else if (window.location.pathname === '/StandardSummaryFatalityReport') {
  render(
    <DBWrapper reduxStore={reduxStore}>
      <StandardSummaryFatalityReportViewer />
    </DBWrapper>
    , rootEl)
} else if (window.location.pathname === '/StandardSummaryTroopAreaReport') {
  render(
    <DBWrapper reduxStore={reduxStore}>
      <StandardSummaryTroopAreaReportViewer />
    </DBWrapper>
    , rootEl)
}else if (
  window === window.parent &&
  window === window.top &&
  !AuthContext.isCallback(window.location.hash)
) {
  // Extra callback logic, only in the actual application, not in iframes in the app
  if (!SessionHelper.isTokenExpired) {
    // check if the expiry time in the storage has not yet passed
    // Having both of these checks is to prevent having a token in localstorage, but no user.
    if (
      !AuthContext.getCachedToken(AdalConfig.clientId) ||
      !AuthContext.getCachedUser()
    ) {
      AuthContext.login();
      // or render something that everyone can see
      // ReactDOM.render(<PublicPartOfApp />, document.getElementById('root'))
    } else {
      AuthContext.acquireToken(
        AdalConfig.endpoints.api,
        (message, token, msg) => {
          if (token) {
            // Set the expiry time out 30 minutes from now
            SessionHelper.setExpiry();
            // At the initialisation of our app we start a session timeout function, which will be triggered after the amount of minutes set in our adal config.
            // But first we'll provide a callback to execute at the timeout.
            // On the callback we will check the token expiry time and log out the user if necessary.
            SessionHelper.expiryTimeoutCallback = function () {
              if (SessionHelper.isTokenExpiredOrNull) {
                // clear the session helper
                SessionHelper.removeExpiry();
                SessionHelper.stopExpiryTimeout();
                AuthContext.logOut();
              } else {
                SessionHelper.resetExpiryTimeout(); // try again later
              }
            };
            // Then we'll start the timer
            SessionHelper.startExpiryTimeout();
            // After we've prepared everything for the session helper, we render the authenticated part of our app
            render(
              <Provider store={reduxStore}>
                <I18nextProvider i18n={i18n}>
                  <Router>
                    <AppContainer>
                      <DBWrapper reduxStore={reduxStore}>
                        <App />
                      </DBWrapper>
                    </AppContainer>
                  </Router>
                </I18nextProvider>
              </Provider>,
              rootEl
            );
          }
        }
      );
    }
  } else {
    // clear the expiry value from storage, stop the timeout function and logout the user
    SessionHelper.removeExpiry();
    SessionHelper.stopExpiryTimeout();
    AuthContext.logOut();
  }
}

// Hot Module Replacement API
declare let module: { hot: any };

if (module.hot) {
  module.hot.accept('./components/Application', () => {
    const NewApp = require('./components/Application').default;

    render(
      <Provider store={reduxStore}>
        <I18nextProvider i18n={i18n}>
          <Router>
            <AppContainer>
              <DBWrapper reduxStore={reduxStore}>
                <NewApp />
              </DBWrapper>
            </AppContainer>
          </Router>
        </I18nextProvider>
      </Provider>,
      rootEl
    );
  });
}
